import type { ArtifactType } from "./ArtifactStore";

export interface EditedArtifact {
  blockId: string;
  editedContent: JSONContent;
  originalContent: any;
  editedAt: Date;
  artifactTitle: string;
  artifactType: ArtifactType;
}

export type JSONContent = {
  type?: string;
  attrs?: Record<string, any>;
  content?: JSONContent[];
  marks?: {
    type: string;
    attrs?: Record<string, any>;
    [key: string]: any;
  }[];
  text?: string;
  [key: string]: any;
};

const DB_NAME = "storytell-artifacts";
const DB_VERSION = 1;
const STORE_NAME = "editedArtifacts";

class IndexedDBError extends Error {
  constructor(
    message: string,
    public readonly originalError?: Error,
  ) {
    super(message);
    this.name = "IndexedDBError";
  }
}

const openDatabase = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new IndexedDBError("IndexedDB is not available in this environment"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new IndexedDBError("Failed to open IndexedDB", request.error || undefined));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: "blockId" });
        objectStore.createIndex("editedAt", "editedAt", { unique: false });
      }
    };
  });
};

const handleTransaction = <T>(
  storeName: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  return openDatabase()
    .then((db) => {
      return new Promise<T>((resolve, reject) => {
        try {
          const transaction = db.transaction(storeName, mode);
          const store = transaction.objectStore(storeName);
          const request = operation(store);

          request.onsuccess = () => {
            resolve(request.result);
            db.close();
          };

          request.onerror = () => {
            const error = request.error;
            if (error?.name === "QuotaExceededError") {
              reject(
                new IndexedDBError(
                  "Storage quota exceeded. Please free up space or clear old edits.",
                  error || undefined,
                ),
              );
            } else {
              reject(new IndexedDBError("Transaction failed", error || undefined));
            }
            db.close();
          };

          transaction.onerror = () => {
            const error = transaction.error;
            if (error?.name === "QuotaExceededError") {
              reject(
                new IndexedDBError(
                  "Storage quota exceeded. Please free up space or clear old edits.",
                  error || undefined,
                ),
              );
            } else {
              reject(new IndexedDBError("Transaction error", error || undefined));
            }
            db.close();
          };
        } catch (error) {
          db.close();
          reject(
            error instanceof IndexedDBError
              ? error
              : new IndexedDBError("Failed to execute transaction", error as Error),
          );
        }
      });
    })
    .catch((error) => {
      throw error instanceof IndexedDBError ? error : new IndexedDBError("Failed to open database", error as Error);
    });
};

export const saveArtifactEdit = async (
  blockId: string,
  content: JSONContent,
  originalContent: JSONContent | null,
  title: string,
  type: ArtifactType,
): Promise<void> => {
  if (!blockId) {
    throw new IndexedDBError("blockId is required");
  }

  const artifact: EditedArtifact = {
    blockId,
    editedContent: content,
    originalContent: originalContent,
    editedAt: new Date(),
    artifactTitle: title,
    artifactType: type,
  };

  await handleTransaction(STORE_NAME, "readwrite", (store) => store.put(artifact));
};

export const loadArtifactEdit = async (blockId: string): Promise<EditedArtifact | null> => {
  if (!blockId) {
    throw new IndexedDBError("blockId is required");
  }

  try {
    const result = await handleTransaction(STORE_NAME, "readonly", (store) => store.get(blockId));

    if (!result) {
      return null;
    }

    return {
      ...result,
      editedAt: new Date(result.editedAt),
    };
  } catch (error) {
    console.error("Failed to load artifact edit:", error);
    return null;
  }
};

export const clearArtifactEdit = async (blockId: string): Promise<void> => {
  if (!blockId) {
    throw new IndexedDBError("blockId is required");
  }

  await handleTransaction(STORE_NAME, "readwrite", (store) => store.delete(blockId));
};

export const getAllEditedArtifacts = async (): Promise<EditedArtifact[]> => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const results = request.result.map((artifact: EditedArtifact) => ({
          ...artifact,
          editedAt: new Date(artifact.editedAt),
        }));
        resolve(results);
        db.close();
      };

      request.onerror = () => {
        reject(new IndexedDBError("Failed to get all artifacts", request.error || undefined));
        db.close();
      };
    } catch (error) {
      db.close();
      reject(
        error instanceof IndexedDBError
          ? error
          : new IndexedDBError("Failed to retrieve all artifacts", error as Error),
      );
    }
  });
};

export const cleanupOldEdits = async (daysOld: number): Promise<void> => {
  if (daysOld < 0) {
    throw new IndexedDBError("daysOld must be a positive number");
  }

  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index("editedAt");

      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const request = index.openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const artifact = cursor.value as EditedArtifact;
          const editedAt = new Date(artifact.editedAt);

          if (editedAt < cutoffDate) {
            cursor.delete();
          }

          cursor.continue();
        }
      };

      transaction.oncomplete = () => {
        resolve();
        db.close();
      };

      transaction.onerror = () => {
        const error = transaction.error;
        if (error?.name === "QuotaExceededError") {
          reject(
            new IndexedDBError("Storage quota exceeded. Please free up space or clear old edits.", error || undefined),
          );
        } else {
          reject(new IndexedDBError("Cleanup transaction failed", error || undefined));
        }
        db.close();
      };
    } catch (error) {
      db.close();
      reject(
        error instanceof IndexedDBError ? error : new IndexedDBError("Failed to cleanup old edits", error as Error),
      );
    }
  });
};
